import { useI18n } from 'vue-i18n';

import { parsePhoneNumber } from 'libphonenumber-js';
import moment from 'moment-timezone';

import { useAskChangesFormater } from '@/composables/askChanges/useAskChangesFormater';
import { formattedAddress } from '@/helpers/address';
import { capitalizeFirstLetter } from '@/helpers/string';
import { normalizeJobExperience } from '@/helpers/workerJobExperience';
import {
	type AskedChangeDisplayItem,
	AskedChangeFieldEnum,
	type WorkerAskedChangeEntry,
	type WorkerAskedChangesResponse,
} from '@/modules/worker/components/workerAskChanges/types';

import type { WorkerDetail } from '@/modules/worker/types';
import type { EvoliaAddress } from '@/types/address';
import type { IWorkerJob } from '@/types/job';

import { normalizeWorkerJobsArray } from '@/modules/worker/components/workerAskChanges/workerAskedChangesHelpers';

const toWorkerJob = (row: {
	jobId: string;
	experience?: string;
	degreeLevel?: string;
}): IWorkerJob => ({
	jobId: row.jobId,
	experience: normalizeJobExperience(row.experience),
	degreeLevel: row.degreeLevel,
});

const formatAskedChangeDisplayValue = (value: unknown): string | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === 'string') return value;
	if (typeof value === 'number' || typeof value === 'boolean') {
		return String(value);
	}
	if (typeof value === 'object' && !Array.isArray(value)) {
		const formatted = formattedAddress(value as EvoliaAddress).trim();
		if (formatted) return formatted;
		return JSON.stringify(value);
	}
	return String(value);
};

export const useWorkerAskedChangesDisplay = () => {
	const { t } = useI18n();
	const { formatJob } = useAskChangesFormater();

	const formatGenderLabel = (value: unknown): string => {
		if (value === null || value === undefined || value === '') {
			return t('page.worker.askedChanges.genderNotSpecified');
		}
		const normalized = String(value).toLowerCase();
		if (normalized === 'male') return t('page.worker.askedChanges.genderMale');
		if (normalized === 'female')
			return t('page.worker.askedChanges.genderFemale');
		return String(value);
	};

	const mapJobsToDisplayItems = (
		_worker: WorkerDetail,
		entry: WorkerAskedChangeEntry
	): AskedChangeDisplayItem[] => {
		const items: AskedChangeDisplayItem[] = [];
		const currentNormalized = normalizeWorkerJobsArray(entry.currentValue);
		const proposedNormalized = normalizeWorkerJobsArray(entry.proposedValue);

		const currentAsI = currentNormalized.map(toWorkerJob);
		const proposedAsI = proposedNormalized.map(toWorkerJob);

		const newJobs = proposedAsI.filter(
			(elem) => !currentAsI.some((job) => job.jobId === elem.jobId)
		);
		const deleteJobs = currentAsI.filter(
			(elem) => !proposedAsI.some((job) => job.jobId === elem.jobId)
		);
		const updatedJobs = proposedAsI.filter((elem) =>
			currentAsI.some((job) => job.jobId === elem.jobId)
		);

		newJobs.forEach((job) => {
			items.push({
				field: AskedChangeFieldEnum.JOBS,
				oldValue: null,
				newValue: formatJob(job),
			});
		});
		deleteJobs.forEach((job) => {
			items.push({
				field: AskedChangeFieldEnum.JOBS,
				oldValue: formatJob(job),
				newValue: null,
			});
		});
		updatedJobs.forEach((job) => {
			const newValue = formatJob(job);
			const oldValue = formatJob(
				currentAsI.find((wjob) => wjob.jobId === job.jobId)
			);
			if (newValue !== oldValue) {
				items.push({
					field: AskedChangeFieldEnum.JOBS,
					oldValue,
					newValue,
				});
			}
		});

		return items;
	};

	const mapScalarEntry = (
		worker: WorkerDetail,
		entry: WorkerAskedChangeEntry
	): AskedChangeDisplayItem[] => {
		const items: AskedChangeDisplayItem[] = [];
		const field = entry.field;

		switch (field) {
			case AskedChangeFieldEnum.PHONE: {
				const oldRaw = entry.currentValue;
				const newRaw = entry.proposedValue;
				let oldValue: string | null = null;
				let newValue: string | null = null;
				try {
					if (typeof oldRaw === 'string' && oldRaw) {
						oldValue = parsePhoneNumber(oldRaw, 'FR').formatNational();
					} else if (oldRaw != null) {
						oldValue = String(oldRaw);
					}
				} catch {
					oldValue = oldRaw != null ? String(oldRaw) : null;
				}
				try {
					if (typeof newRaw === 'string' && newRaw) {
						newValue = parsePhoneNumber(newRaw, 'FR').formatNational();
					} else if (newRaw != null) {
						newValue = String(newRaw);
					}
				} catch {
					newValue = newRaw != null ? String(newRaw) : null;
				}
				items.push({ field, oldValue, newValue });
				break;
			}
			case AskedChangeFieldEnum.BIRTH_DATE: {
				const oldMs =
					typeof entry.currentValue === 'number'
						? entry.currentValue
						: typeof entry.currentValue === 'string'
							? Number(entry.currentValue)
							: undefined;
				const newMs =
					typeof entry.proposedValue === 'number'
						? entry.proposedValue
						: typeof entry.proposedValue === 'string'
							? Number(entry.proposedValue)
							: undefined;
				items.push({
					field,
					oldValue:
						oldMs != null && !Number.isNaN(oldMs)
							? moment(oldMs).format('YYYY-MM-DD')
							: null,
					newValue:
						newMs != null && !Number.isNaN(newMs)
							? moment(newMs).format('YYYY-MM-DD')
							: null,
				});
				break;
			}
			case AskedChangeFieldEnum.GENDER:
				items.push({
					field,
					oldValue: formatGenderLabel(entry.currentValue),
					newValue: formatGenderLabel(entry.proposedValue),
				});
				break;
			case AskedChangeFieldEnum.FIRSTNAME:
				items.push({
					field,
					oldValue: capitalizeFirstLetter(
						String(entry.currentValue ?? worker.firstname ?? '')
					),
					newValue: capitalizeFirstLetter(String(entry.proposedValue ?? '')),
				});
				break;
			case AskedChangeFieldEnum.LASTNAME:
				items.push({
					field,
					oldValue: capitalizeFirstLetter(
						String(entry.currentValue ?? worker.lastname ?? '')
					),
					newValue: capitalizeFirstLetter(String(entry.proposedValue ?? '')),
				});
				break;
			case AskedChangeFieldEnum.BIRTH_PLACE:
			case AskedChangeFieldEnum.ADDRESS:
				items.push({
					field,
					oldValue: formatAskedChangeDisplayValue(entry.currentValue),
					newValue: formatAskedChangeDisplayValue(entry.proposedValue),
				});
				break;
			default:
				items.push({
					field,
					oldValue: formatAskedChangeDisplayValue(entry.currentValue),
					newValue: formatAskedChangeDisplayValue(entry.proposedValue),
				});
		}

		return items;
	};

	const askedChangesApiToDisplayItems = (
		worker: WorkerDetail,
		response: WorkerAskedChangesResponse
	): AskedChangeDisplayItem[] => {
		const result: AskedChangeDisplayItem[] = [];
		for (const entry of response.changes) {
			if (entry.field === AskedChangeFieldEnum.JOBS) {
				result.push(...mapJobsToDisplayItems(worker, entry));
			} else {
				result.push(...mapScalarEntry(worker, entry));
			}
		}
		return result;
	};

	return { askedChangesApiToDisplayItems };
};
